import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { mitraFont } from '../../assets/fonts/Mitra/Mitra'
import { titrFont } from '../../assets/fonts/Titr/Titr'
import { vazirFont } from '../../assets/fonts/vazir-font-v18.0.0/Vazir'
import { gregorianToJalali, thousandsSeparate, toFarsiDigits } from '../../assets/utils'
import { fetchReportQr } from '../../assets/excelUtils'
import { addPdfFooter } from '../../assets/reportFooter'

const F = (v) => toFarsiDigits(thousandsSeparate(v || 0))

/**
 * چاپِ گزارشِ صورت‌وضعیت (PDF با کیفیتِ بالا):
 *   بالا ⇒ مشخصاتِ پیمانکار | مشخصاتِ قرارداد | مشخصاتِ صورت‌وضعیت
 *   پایین ⇒ سه سطرِ تجمعی/قبلی/همین با ۹ ستون
 */
export async function printRequestReport(report) {
  if (!report) return
  const qrData = report.contract?.id ? await fetchReportQr(report.contract.id) : null

  const doc = new jsPDF('landscape')
  doc.addFileToVFS('Vazir.ttf', vazirFont)
  doc.addFont('Vazir.ttf', 'Vazir', 'normal')
  doc.addFileToVFS('Titr.ttf', titrFont)
  doc.addFont('Titr.ttf', 'Titr', 'normal')
  doc.addFileToVFS('Mitra.ttf', mitraFont)
  doc.addFont('Mitra.ttf', 'Mitra', 'normal')

  const pageW = doc.internal.pageSize.width
  const right = pageW - 12
  const textH = doc.getTextDimensions('ب').h
  let y = 6 * textH

  // ── سربرگ ─────────────────────────────────────────────
  doc.setFont('Titr')
  doc.setFontSize(16)
  doc.text(toFarsiDigits(report.contract?.organization || ''), pageW / 2, y, 'center')
  y += textH * 1.1

  doc.setFontSize(13)
  doc.text('گزارش صورت وضعیت', pageW / 2, y, 'center')
  y += textH * 1.4

  const ctr = report.contractor || {}
  const cw = report.contract || {}
  const rq = report.request || {}

  doc.setFont('Mitra')
  doc.setFontSize(10.5)

  // ── سه ستونِ بالای جدول (مثلِ نمایشِ صفحه) ────────────
  // هر ستون یک رنگ دارد؛ در چاپِ تک‌رنگ، عنوانِ هر ستون و کادرِ آن
  // تفکیک را حفظ می‌کند.
  const ctrLines = [
    `نام: ${toFarsiDigits(ctr.name || '—')}`,
    `شناسه ملی: ${toFarsiDigits(ctr.national_code || '—')}`,
    `کد اقتصادی: ${toFarsiDigits(ctr.economic_code || '—')}`,
  ]
  const cwLines = [
    `شماره: ${toFarsiDigits(cw.number || '—')}`,
    `تاریخ: ${toFarsiDigits(cw.date || '—')}`,
    `موضوع: ${toFarsiDigits(cw.subject || 'نامشخص')}`,
    `سازمان: ${toFarsiDigits(cw.organization || '—')}`,
    `مشاور: ${toFarsiDigits(cw.consultant || '—')}`,
    `ناظر: ${toFarsiDigits(cw.supervisor || '—')}`,
    `مبلغ اولیه: ${F(cw.contract_cost)}`,
    `افزایش/کاهش: ${F(cw.increase_decrease_cost)}`,
    `مبلغ قرارداد: ${F(cw.contract_total)}`,
  ]
  const rqLines = [
    `نوع: ${toFarsiDigits(rq.type || '—')}`,
    `شماره: ${toFarsiDigits(rq.number || '—')}`,
    `شماره نامه: ${toFarsiDigits(rq.letter_number || '—')}`,
    `تاریخ نامه: ${toFarsiDigits(rq.letter_date || '—')}`,
    `از تاریخ: ${toFarsiDigits(rq.from_date || '—')}`,
    `تا تاریخ: ${toFarsiDigits(rq.to_date || '—')}`,
    `تاریخ گزارش: ${toFarsiDigits(gregorianToJalali(new Date().toISOString().slice(0, 10)))}`,
  ]

  const headCells = ['مشخصات پیمانکار', 'مشخصات قرارداد', 'مشخصات صورت‌وضعیت'].reverse()
  const bodyCells = [ctrLines.join('\n'), cwLines.join('\n'), rqLines.join('\n')].reverse()
  const COLORS = [[176, 58, 10], [1, 87, 56], [26, 35, 126]]   // نارنجی، سبز، آبی
  const FILLS = [[255, 243, 224], [232, 245, 233], [232, 234, 246]]

  doc.autoTable({
    startY: y,
    head: [headCells],
    body: [bodyCells],
    theme: 'grid',
    styles: {
      font: 'Mitra', fontSize: 9.5, halign: 'right', valign: 'top',
      lineWidth: 0.5, lineColor: 150, cellPadding: 2.5,
    },
    headStyles: {
      font: 'Mitra', fontSize: 10.5, halign: 'center', valign: 'middle',
      fillColor: [26, 35, 126], textColor: 255, fontStyle: 'normal', lineWidth: 0.5, lineColor: 255,
    },
    bodyStyles: { font: 'Mitra', fontSize: 9.5, halign: 'right', valign: 'top', textColor: 0 },
    columnStyles: {
      0: { fillColor: FILLS[0] },
      1: { fillColor: FILLS[1] },
      2: { fillColor: FILLS[2] },
    },
    didParseCell: (data) => {
      if (data.section === 'body') {
        const c = COLORS[data.column] || [0, 0, 0]
        data.cell.styles.textColor = c
      }
    },
  })
  y = doc.autoTable.previous.finalY + textH * 1.2

  doc.setFont('Mitra')
  doc.setFontSize(9)
  doc.text('ارقام به ریال', right, y, 'right')
  y += textH * 1.1


  // ── جدولِ سه‌سطری ─────────────────────────────────────
  const cols = report.columns || []
  const head = ['شرح', ...cols.map((c) => c.label)].reverse()
  const body = (report.rows || []).map((row) => [
    ...cols.map((c) => F((row.values || {})[c.key])),
    toFarsiDigits(row.label),
  ].reverse())

  doc.autoTable({
    startY: y + 2,
    styles: {
      font: 'Mitra', halign: 'center', valign: 'middle',
      lineWidth: 0.4, lineColor: 180, fontSize: 9.5,
    },
    head: [head],
    body,
    theme: 'grid',
    margin: { left: 10, right: 10 },
    headStyles: {
      font: 'Mitra', fontSize: 9.5, halign: 'center', valign: 'middle',
      fillColor: [26, 35, 126], textColor: 255, fontStyle: 'normal',
      lineWidth: 0.4, lineColor: 255,
    },
    bodyStyles: {
      font: 'Mitra', fontSize: 9.5, halign: 'center', valign: 'middle',
      fillColor: 255, textColor: 0, fontStyle: 'normal',
    },
    alternateRowStyles: { fillColor: [244, 246, 251] },
    columnStyles: {
      0: { cellWidth: 58, halign: 'right', fontSize: 9 },
    },
  })

  let footY = doc.autoTable.previous.finalY + textH * 1.4
  doc.setFont('Mitra')
  doc.setFontSize(9)
  doc.text('سامانه ارجان پیمان - شرکت ارجان آرایه پویا', right, footY, 'right')
  footY += textH * 0.9
  if (qrData) {
    doc.text('با اسکن کیوآرکد، این گزارش در موبایل قابل مشاهده است.', right, footY, 'right')
    footY += textH * 0.9
    try { doc.addImage(qrData, 'PNG', 12, 10, 24, 24) } catch (e) { /* اختیاری */ }
  }

  addPdfFooter(doc)
  doc.output('dataurlnewwindow', `request-report-${rq.number || ''}.pdf`)
}
